import React, { useEffect, useState } from 'react';
import { Button, Input, Select, notification, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { clientsApi } from '../api';
import StepDrawer from '../other/StepDrawer';
import { Field, EntityChoice, Summary, entityName, apiErrorMessage } from '../other/contractFields';
import {
  convertToCamelCase,
  convertFirstLetterToCamelCase,
} from '../other/usefulFunctions';
import './clientDrawer.css';

const { TextArea } = Input;
const alphaRegex = /^[a-zA-Z\s]+$/;
const alphaNumericRegex = /^[a-zA-Z0-9\s]+$/;
const alnumRegex = /^[a-zA-Z0-9]+$/;
const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9]{1}[A-Z]{1}[A-Z 0-9]{1}$/;
const tanRegex = /^[A-Z]{4}[0-9]{5}[A-Z]$/;
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,7}$/;
const indiaPhoneRegex = /^(\+?91[-\s]?)?[6-9]\d{9}$/;
const usPhoneRegex = /^(\+?1[-.\s]?)?\(?([2-9]\d{2})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})$/;
const pinCodeRegex = /^[1-9][0-9]{5}$/; 
const zipRegex = /^\d{5}(-\d{4})?$/; 

const toPin = (v) => String(v ?? '').replace(/\D/g, '').replace(/^0+/, '').slice(0, 6);
const toZip = (v) => {
  const digits = String(v ?? '').replace(/\D/g, '').slice(0, 9);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};
const hasValue = (v) => String(v ?? '').trim() !== '';

const POC_TYPES = ['Finance', 'Sales', 'Admin', 'Other'];
const DAY_OPTIONS = Array.from({ length: 28 }, (_, i) => ({
  label: String(i + 1).padStart(2, '0'),
  value: i + 1,
}));

const emptyLocation = () => ({
  location: '',
  address: '',
  GSTIN: '',
  city: '',
  state: '',
  country: '',
  pinCode: '',
});
const emptyPoc = () => ({ pocType: 'Finance', pocName: '', pocEmail: '', pocNumber: '' });

const emptyClient = () => ({
  seId: 1,
  legalEntityName: '',
  address: '',
  city: '',
  state: '',
  country: '',
  pinCode: '',
  GSTINDetails: [emptyLocation()],
  TANNumber: '',
  TaxId: '',
  suggestedInvoiceDate: '',
  poc: [emptyPoc()],
});
const filterEmptyValues = (obj) => {
  const out = Array.isArray(obj) ? [] : {};
  Object.keys(obj).forEach((key) => {
    if (Array.isArray(obj[key])) {
      out[key] = obj[key].map((item) => filterEmptyValues(item));
    } else if (obj[key] !== '' && obj[key] !== null && obj[key] !== undefined) {
      out[key] = obj[key];
    }
  });
  return out;
};
const ClientDrawer = ({ open, onClose, clientId }) => {
  const isEdit = !!clientId;
  const [step, setStep] = useState(0);
  const [client, setClient] = useState(emptyClient());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const isIndia = client.seId === 1;

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setErrors({});
    setClient(emptyClient());
    if (!isEdit) return;

    setLoading(true);
    clientsApi
      .getClientData(clientId, '')
      .then((res) => {
        const data = res.data.data[0];
        if (!data) return;
        setClient({
          seId: data.seId,
          legalEntityName: data.legalEntityName || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          country: data.country || '',
          pinCode: data.seId === 0 ? toZip(data.pinCode) : data.pinCode || '',
          TANNumber: data.TANNumber || '',
          TaxId: data.TaxId || '',
          GSTINDetails: data.GSTINDetails?.length
            ? data.GSTINDetails.map((g) => ({
                address: g.address || '',
                GSTIN: g.GSTIN || '',
                location: g.location || '',
                city: g.city || '',
                state: g.state || '',
                country: g.country || '',
                pinCode: g.pinCode != null ? String(g.pinCode) : '',
              }))
            : [emptyLocation()],
          suggestedInvoiceDate: data.suggestedInvoiceDate ?? '',
          poc: data.poc?.length
            ? data.poc.map((p) => ({
                pocType: p.pocType || 'Finance',
                pocName: p.pocName || '',
                pocEmail: p.pocEmail || '',
                pocNumber: p.pocNumber || '',
              }))
            : [emptyPoc()],
        });
      })
      .catch((err) => console.error('Error fetching client:', err))
      .finally(() => setLoading(false));
  }, [open, clientId, isEdit]);

  const clearError = (...keys) =>
    setErrors((prev) => {
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      return next;
    });

  const setField = (key, value) => {
    setClient((prev) => ({ ...prev, [key]: value }));
    clearError(key);
  };

  const setIfValid = (key, value, pattern, maxLength) => {
    if (value === '' || (pattern.test(value) && (!maxLength || value.length <= maxLength))) {
      setField(key, value);
    }
  };

  const setLocation = (index, key, value) => {
    setClient((prev) => ({
      ...prev,
      GSTINDetails: prev.GSTINDetails.map((g, i) => (i === index ? { ...g, [key]: value } : g)),
    }));
    clearError(`gst.${index}.${key}`);
  };

  const setPoc = (index, key, value) => {
    setClient((prev) => ({
      ...prev,
      poc: prev.poc.map((p, i) => (i === index ? { ...p, [key]: value } : p)),
    }));
    clearError(`poc.${index}.${key}`);
  };

  const changeEntity = (seId) => {
    setClient((prev) =>
      seId === 0
        ? { ...prev, seId, GSTINDetails: [emptyLocation()], TANNumber: '' }
        : { ...prev, seId, address: '', city: '', state: '', country: '', pinCode: '', TaxId: '' }
    );
    setErrors({});
  };

  const stepErrors = (index) => {
    const e = {};
    if (index === 0) {
      if (!hasValue(client.legalEntityName)) e.legalEntityName = 'Enter the legal entity name';
      if (isIndia) {
        if (!hasValue(client.TANNumber)) e.TANNumber = 'Enter the TAN';
        else if (!tanRegex.test(client.TANNumber.trim()))
          e.TANNumber = 'A TAN is 4 letters, 5 digits, then 1 letter (e.g. PNEA18234G)';
      } else if (!hasValue(client.TaxId)) {
        e.TaxId = 'Enter the tax ID';
      }
      if (!hasValue(client.suggestedInvoiceDate)) e.suggestedInvoiceDate = 'Choose the invoice day';
    }
    if (index === 1) {
      if (isIndia) {
        client.GSTINDetails.forEach((g, i) => {
          if (!hasValue(g.location)) e[`gst.${i}.location`] = 'Required';
          if (g.GSTIN && !gstRegex.test(g.GSTIN)) e[`gst.${i}.GSTIN`] = 'Not a valid GSTIN';
          if (!hasValue(g.address)) e[`gst.${i}.address`] = 'Required';
          if (!hasValue(g.city)) e[`gst.${i}.city`] = 'Required';
          if (!hasValue(g.state)) e[`gst.${i}.state`] = 'Required';
          if (!hasValue(g.country)) e[`gst.${i}.country`] = 'Required';
          if (!pinCodeRegex.test(String(g.pinCode))) e[`gst.${i}.pinCode`] = 'Enter a 6-digit PIN';
        });
      } else {
        if (!hasValue(client.address)) e.address = 'Required';
        if (!hasValue(client.city)) e.city = 'Required';
        if (!hasValue(client.state)) e.state = 'Required';
        if (!hasValue(client.country)) e.country = 'Required';
        if (!zipRegex.test(String(client.pinCode))) e.pinCode = 'Enter 12345 or 12345-6789';
      }
    }
    if (index === 2) {
      client.poc.forEach((p, i) => {
        if (!hasValue(p.pocName)) e[`poc.${i}.pocName`] = 'Required';
        if (!emailRegex.test(p.pocEmail)) e[`poc.${i}.pocEmail`] = 'Enter a valid email';
        const phoneValue = String(p.pocNumber).trim();
        if (isIndia) {
          if (!indiaPhoneRegex.test(phoneValue))
            e[`poc.${i}.pocNumber`] = 'Enter a valid 10-digit mobile number (e.g. 98765 43210)';
        } else if (indiaPhoneRegex.test(phoneValue)) {
          e[`poc.${i}.pocNumber`] = 'This looks like an Indian number — enter a US number (e.g. (212) 555-0198)';
        } else if (!usPhoneRegex.test(phoneValue)) {
          e[`poc.${i}.pocNumber`] = 'Enter a valid US number (e.g. (212) 555-0198)';
        }
      });
    }
    return e;
  };

  const goNext = () => {
    const e = stepErrors(step);
    setErrors(e);
    if (Object.keys(e).length === 0) setStep(step + 1);
  };

  const goToStep = (index) => {
    if (index <= step) {
      setErrors({});
      setStep(index);
      return;
    }
    for (let i = step; i < index; i++) {
      const e = stepErrors(i);
      if (Object.keys(e).length) {
        setErrors(e);
        setStep(i);
        return;
      }
    }
    setErrors({});
    setStep(index);
  };

  const submit = () => {
    for (let i = 0; i < 3; i++) {
      const e = stepErrors(i);
      if (Object.keys(e).length) {
        setErrors(e);
        setStep(i);
        return;
      }
    }

    const postData = filterEmptyValues(client);
    if (postData.pinCode) postData.pinCode = String(postData.pinCode).replace(/\D/g, '');

    setSaving(true);
    const request = isEdit
      ? clientsApi.updateClientData(clientId, postData)
      : clientsApi.addClientData(postData);
    request
      .then(() => {
        notification.success({ message: isEdit ? 'Client updated' : 'Client created' });
        onClose(true);
      })
      .catch((err) => {
        notification.error({ message: apiErrorMessage(err, 'Could not save the client') });
      })
      .finally(() => setSaving(false));
  };

  const textInput = (key, label, { pattern, maxLength, transform = (v) => v, placeholder } = {}) => (
    <Field label={label} required error={errors[key]}>
      <Input
        size="large"
        value={client[key]}
        status={errors[key] ? 'error' : ''}
        placeholder={placeholder}
        onChange={(e) => {
          const value = transform(e.target.value);
          if (pattern) setIfValid(key, value, pattern, maxLength);
          else setField(key, value);
        }}
      />
    </Field>
  );

  const locationInput = (index, key, label, { pattern, maxLength, transform = (v) => v, placeholder, required = true } = {}) => {
    const errKey = `gst.${index}.${key}`;
    return (
      <Field label={label} required={required} error={errors[errKey]}>
        <Input
          value={client.GSTINDetails[index][key]}
          status={errors[errKey] ? 'error' : ''}
          placeholder={placeholder}
          onChange={(e) => {
            const value = transform(e.target.value);
            if (!pattern || value === '' || (pattern.test(value) && (!maxLength || value.length <= maxLength))) {
              setLocation(index, key, value);
            }
          }}
        />
      </Field>
    );
  };

  const steps = [
    {
      key: 'company',
      title: 'Company',
      content: loading ? (
        <Spin />
      ) : (
        <div className="sd-form">
          <EntityChoice value={client.seId} onChange={changeEntity} disabled={isEdit} />
          <Field
            label="Legal entity name"
            required
            error={errors.legalEntityName}
            help={isEdit ? 'The legal name cannot be changed after the client is created.' : undefined}
          >
            <Input
              size="large"
              value={client.legalEntityName}
              status={errors.legalEntityName ? 'error' : ''}
              placeholder="As on the client's registration"
              disabled={isEdit}
              onChange={(e) => setIfValid('legalEntityName', convertToCamelCase(e.target.value), alphaNumericRegex)}
            />
          </Field>
          <div className="sd-row">
            {isIndia
              ? textInput('TANNumber', 'TAN', {
                  pattern: alnumRegex,
                  maxLength: 10,
                  transform: (v) => v.toUpperCase().replace(/\s/g, ''),
                  placeholder: 'e.g. PNEA18234G',
                })
              : textInput('TaxId', 'Tax ID', {
                  pattern: alnumRegex,
                  maxLength: 10,
                  transform: (v) => convertToCamelCase(v).replace(/\s/g, ''),
                  placeholder: 'Tax ID',
                })}
            <Field
              label="Invoice day"
              required
              error={errors.suggestedInvoiceDate}
              help="Day of the month this client is usually invoiced"
            >
              <Select
                size="large"
                placeholder="Choose a day"
                value={client.suggestedInvoiceDate === '' ? undefined : client.suggestedInvoiceDate}
                onChange={(day) => setField('suggestedInvoiceDate', day)}
                options={DAY_OPTIONS}
                status={errors.suggestedInvoiceDate ? 'error' : ''}
              />
            </Field>
          </div>
        </div>
      ),
    },
    {
      key: 'addresses',
      title: isIndia ? 'GST locations' : 'Address',
      content: isIndia ? (
        <div className="sd-form">
          {client.GSTINDetails.map((g, index) => (
            <div key={index} className="sd-fieldset">
              <div className="cd-fieldset-head">
                <span className="sd-fieldset-title">
                  {index === 0 ? 'Primary location' : `Location ${index + 1}`}
                </span>
                {client.GSTINDetails.length > 1 && (
                  <Button
                    type="text"
                    aria-label={`Remove location ${index + 1}`}
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      setClient((prev) => ({
                        ...prev,
                        GSTINDetails: prev.GSTINDetails.filter((_, i) => i !== index),
                      }))
                    }
                  />
                )}
              </div>
              <div className="sd-row">
                {locationInput(index, 'location', 'Location name', {
                  pattern: alphaRegex,
                  maxLength: 19,
                  transform: convertFirstLetterToCamelCase,
                  placeholder: 'e.g. Bengaluru office',
                })}
                {locationInput(index, 'GSTIN', 'GSTIN', {
                  pattern: alnumRegex,
                  maxLength: 15,
                  transform: (v) => convertToCamelCase(v).replace(/\s/g, ''),
                  placeholder: 'e.g. 08AAPCS5825D1Z0',
                  required: false,
                })}
              </div>
              <Field label="Address" required error={errors[`gst.${index}.address`]}>
                <TextArea
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  value={g.address}
                  status={errors[`gst.${index}.address`] ? 'error' : ''}
                  placeholder="Building, street, area"
                  onChange={(e) => setLocation(index, 'address', e.target.value)}
                />
              </Field>
              <div className="cd-grid-4">
                {locationInput(index, 'city', 'City', { pattern: alphaRegex, maxLength: 44, transform: convertFirstLetterToCamelCase })}
                {locationInput(index, 'state', 'State', { pattern: alphaRegex, maxLength: 19, transform: convertFirstLetterToCamelCase })}
                {locationInput(index, 'country', 'Country', { pattern: alphaRegex, maxLength: 49, transform: convertFirstLetterToCamelCase })}
                {locationInput(index, 'pinCode', 'PIN', { transform: toPin, placeholder: '560001' })}
              </div>
            </div>
          ))}
          <Button
            icon={<PlusOutlined />}
            onClick={() => setClient((prev) => ({ ...prev, GSTINDetails: [...prev.GSTINDetails, emptyLocation()] }))}
          >
            Add another location
          </Button>
        </div>
      ) : (
        <div className="sd-form">
          <Field label="Address" required error={errors.address}>
            <TextArea
              autoSize={{ minRows: 2, maxRows: 4 }}
              value={client.address}
              status={errors.address ? 'error' : ''}
              placeholder="Building, street, suite"
              onChange={(e) => setField('address', e.target.value)}
            />
          </Field>
          <div className="cd-grid-4">
            {textInput('city', 'City', { pattern: alphaRegex, maxLength: 19, transform: convertFirstLetterToCamelCase })}
            {textInput('state', 'State', { pattern: alphaRegex, maxLength: 19, transform: convertFirstLetterToCamelCase })}
            {textInput('country', 'Country', { pattern: alphaRegex, maxLength: 19, transform: convertFirstLetterToCamelCase })}
            {textInput('pinCode', 'ZIP code', { transform: toZip, placeholder: '12345' })}
          </div>
        </div>
      ),
    },
    {
      key: 'contacts',
      title: 'Contacts',
      content: (
        <div className="sd-form">
          {client.poc.map((p, index) => (
            <div key={index} className="sd-fieldset">
              <div className="cd-fieldset-head">
                <span className="sd-fieldset-title">
                  {index === 0 ? 'Primary contact' : `Contact ${index + 1}`}
                </span>
                {client.poc.length > 1 && (
                  <Button
                    type="text"
                    aria-label={`Remove contact ${index + 1}`}
                    icon={<DeleteOutlined />}
                    onClick={() => setClient((prev) => ({ ...prev, poc: prev.poc.filter((_, i) => i !== index) }))}
                  />
                )}
              </div>
              <div className="sd-row">
                <Field label="Role">
                  <Select
                    value={p.pocType}
                    onChange={(value) => setPoc(index, 'pocType', value ?? '')}
                    options={POC_TYPES.map((t) => ({ value: t, label: t }))}
                  />
                </Field>
                <Field label="Name" required error={errors[`poc.${index}.pocName`]}>
                  <Input
                    value={p.pocName}
                    status={errors[`poc.${index}.pocName`] ? 'error' : ''}
                    placeholder="Full name"
                    onChange={(e) => {
                      const value = convertFirstLetterToCamelCase(e.target.value);
                      if (value === '' || (alphaRegex.test(value) && value.length < 50)) {
                        setPoc(index, 'pocName', value);
                      }
                    }}
                  />
                </Field>
              </div>
              <div className="sd-row">
                <Field label="Email" required error={errors[`poc.${index}.pocEmail`]}>
                  <Input
                    type="email"
                    value={p.pocEmail}
                    status={errors[`poc.${index}.pocEmail`] ? 'error' : ''}
                    placeholder="name@company.com"
                    onChange={(e) => setPoc(index, 'pocEmail', e.target.value.trim())}
                  />
                </Field>
                <Field label="Mobile" required error={errors[`poc.${index}.pocNumber`]}>
                  <Input
                    inputMode="tel"
                    maxLength={20}
                    value={p.pocNumber}
                    status={errors[`poc.${index}.pocNumber`] ? 'error' : ''}
                    placeholder={isIndia ? '+91 98765 43210' : '(212) 555-0198'}
                    onChange={(e) => {
                      const { value } = e.target;
                      if (value === '' || /^[0-9+\-\s().]*$/.test(value)) setPoc(index, 'pocNumber', value);
                    }}
                  />
                </Field>
              </div>
            </div>
          ))}
          <Button
            icon={<PlusOutlined />}
            onClick={() => setClient((prev) => ({ ...prev, poc: [...prev.poc, emptyPoc()] }))}
          >
            Add another contact
          </Button>
        </div>
      ),
    },
    {
      key: 'review',
      title: 'Review',
      content: (
        <div className="sd-form">
          <Summary
            rows={[
              { label: 'Contracting entity', value: entityName(client.seId) },
              { label: 'Legal entity name', value: client.legalEntityName },
              isIndia
                ? { label: 'TAN', value: client.TANNumber }
                : { label: 'Tax ID', value: client.TaxId },
              {
                label: 'Invoice day',
                value: client.suggestedInvoiceDate ? `Day ${client.suggestedInvoiceDate} of the month` : '',
              },
              isIndia
                ? {
                    label: 'GST locations',
                    value: client.GSTINDetails.map((g) =>
                      [g.location, g.GSTIN || 'no GSTIN', g.city].filter(Boolean).join(' · ')
                    ).join('; '),
                  }
                : {
                    label: 'Address',
                    value: [client.address, client.city, client.state, client.country, client.pinCode]
                      .filter(Boolean)
                      .join(', '),
                  },
              {
                label: 'Contacts',
                value: client.poc
                  .map((p) => [p.pocName, p.pocType].filter(Boolean).join(' (') + (p.pocType ? ')' : ''))
                  .join(', '),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <StepDrawer
      open={open}
      width={760}
      title={isEdit ? 'Edit client' : 'New client'}
      subtitle={client.legalEntityName || (isIndia ? 'India client' : 'International client')}
      steps={steps}
      current={step}
      onStepClick={goToStep}
      onBack={() => setStep(step - 1)}
      onNext={goNext}
      onClose={() => onClose(false)}
      submitButton={
        <Button type="primary" onClick={submit} loading={saving}>
          {isEdit ? 'Save changes' : 'Create client'}
        </Button>
      }
    />
  );
};

export default ClientDrawer;
